import { Alert, Box, Button, Grid2 as Grid, Typography } from "@mui/material"
import { Link } from "react-router-dom"
import { useCustomQuery } from "@/hooks/useCustomQuery"
import { DashboardCard } from "@/pages/Dashboard"
import { formatToDevanagariStyle } from "@/utilities/helper"

type DayTakings = {
  payment_methods: { name: string; total: number }[]
  sales_return: number
}

type BranchTakings = DayTakings & {
  id: number
  name: string
  code: string | null
}

type Summary = {
  branch_count: number
  all: DayTakings
  branches: BranchTakings[]
}

type SummaryResponse = { data: Summary }

const money = (amount: number) => `Rs. ${formatToDevanagariStyle(amount)}`

const TakingsSection = ({
  title,
  takings,
  isLoading,
}: {
  title: string
  takings?: DayTakings
  isLoading: boolean
}) => {
  // Like the branch dashboard, a day without sales still shows Cash.
  const paymentMethods = takings?.payment_methods.length
    ? takings.payment_methods
    : [{ name: "Cash", total: 0 }]

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
        {title}
      </Typography>
      <Grid container spacing={3}>
        {paymentMethods.map((method) => (
          <Grid key={method.name} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <DashboardCard
              label={method.name}
              count={money(method.total)}
              isLoading={isLoading}
            />
          </Grid>
        ))}
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <DashboardCard
            label="Sales Return"
            count={money(takings?.sales_return ?? 0)}
            isLoading={isLoading}
          />
        </Grid>
      </Grid>
    </Box>
  )
}

const CompanyDashboard = () => {
  const { data, isLoading } = useCustomQuery(
    ["company-admin-summary"],
    "company-admin/summary"
  )
  const summary = (data as SummaryResponse | undefined)?.data

  return (
    <>
      <Typography
        variant="h6"
        sx={{ fontWeight: "bold", textTransform: "uppercase", mb: 3 }}
      >
        Today's Sales
      </Typography>
      {summary?.branch_count === 0 && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
            <Button component={Link} to="/pos/branches" size="small">
              Add branch
            </Button>
          }
        >
          This company has no branch yet. Add one to start selling: products,
          sales and the website all belong to a branch.
        </Alert>
      )}
      {(isLoading || (summary?.branch_count ?? 0) > 1) && (
        <TakingsSection
          title="All Branches"
          takings={summary?.all}
          isLoading={isLoading}
        />
      )}
      {summary?.branches.map((branch) => (
        <TakingsSection
          key={branch.id}
          title={branch.code ? `${branch.name} (${branch.code})` : branch.name}
          takings={branch}
          isLoading={isLoading}
        />
      ))}
    </>
  )
}

export default CompanyDashboard
